import { useEffect } from 'react';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useOrganizationStore } from '@/stores/organization.store';

/**
 * Sidebar selector for the active organization, which scopes the whole app.
 * Re-validates the persisted selection against the fetched org list: a null
 * or stale id (e.g. the org was deleted, or this is a first visit) falls
 * back to the first organization. Renders nothing while orgs load — the
 * page-level skeletons cover the loading state.
 */
export function OrgSwitcher() {
  const { data: organizations } = useOrganizations();
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId);
  const setActiveOrganizationId = useOrganizationStore((s) => s.setActiveOrganizationId);

  useEffect(() => {
    if (!organizations || organizations.length === 0) return;
    if (!activeOrganizationId || !organizations.some((o) => o.id === activeOrganizationId)) {
      setActiveOrganizationId(organizations[0]!.id);
    }
  }, [organizations, activeOrganizationId, setActiveOrganizationId]);

  if (!organizations || organizations.length === 0) return null;

  return (
    <div style={{ marginBottom: 20 }}>
      <label
        htmlFor="org-switcher-select"
        style={{
          display: 'block',
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          color: 'var(--text-3)',
          marginBottom: 6,
        }}
      >
        Organization
      </label>
      <select
        id="org-switcher-select"
        title="Scopes the teams, projects, and labels you see to this organization"
        value={activeOrganizationId ?? organizations[0]!.id}
        onChange={(e) => setActiveOrganizationId(e.target.value)}
        style={{
          width: '100%',
          padding: '8px 10px',
          borderRadius: 7,
          border: '1px solid var(--border)',
          background: 'var(--bg)',
          color: 'var(--text-2)',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {organizations.map((org) => (
          <option key={org.id} value={org.id}>
            {org.name}
          </option>
        ))}
      </select>
    </div>
  );
}
